import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files
import en from '../../translations/en.js';
import es from '../../translations/es.js';
import cs from '../../translations/cs.js';
import da from '../../translations/da.js';
import de from '../../translations/de.js';
import fi from '../../translations/fi.js';
import fr from '../../translations/fr.js';
import it from '../../translations/it.js';
import ja from '../../translations/ja.js';
import ko from '../../translations/ko.js';
import nb from '../../translations/nb.js';
import pl from '../../translations/pl.js';
import ptBR from '../../translations/pt-BR.js';
import ptPT from '../../translations/pt-PT.js';
import th from '../../translations/th.js';
import tr from '../../translations/tr.js';
import vi from '../../translations/vi.js';
import zhCN from '../../translations/zh-CN.js';
import zhTW from '../../translations/zh-TW.js';

// Get locale from Shopify controller data attributes (primary) or URL parameters (fallback)
const getLocaleFromUrl = () => {
  // FOR TESTING: Uncomment the line below to force Spanish
  // return 'es';
  
  // First priority: Get locale from Shopify controller data attributes
  const shopifyAppData = document.getElementById('shopify-app-init');
  if (shopifyAppData && shopifyAppData.dataset) {
    const currentLocale = shopifyAppData.dataset.currentLocale;
    const shopDefaultLocale = shopifyAppData.dataset.shopDefaultLocale;
    
    console.log('🌍 Debug locale detection:');
    console.log('  - currentLocale from Shopify:', currentLocale);
    console.log('  - shopDefaultLocale from Shopify:', shopDefaultLocale);
    
    // Prioritize shopDefaultLocale (shop's configured default) over currentLocale (which may be set by Shopify to 'en')
    if (shopDefaultLocale && shopDefaultLocale !== 'undefined') {
      console.log('🌍 Using shop default locale:', shopDefaultLocale);
      return shopDefaultLocale;
    }
    
    if (currentLocale && currentLocale !== 'undefined') {
      console.log('🌍 Using current locale from Shopify controller:', currentLocale);
      return currentLocale;
    }

  }
  
  // Second priority: Check URL search parameters
  const urlParams = new URLSearchParams(window.location.search);
  const locale = urlParams.get('locale');

  // Third priority: Check hash parameters (Shopify sometimes uses hash routing)
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const hashLocale = hashParams.get('locale');

  // Fourth priority: Check parent window for embedded app context
  let parentLocale = null;
  try {
    if (window.parent && window.parent !== window) {
      const parentParams = new URLSearchParams(window.parent.location.search);
      parentLocale = parentParams.get('locale');
    }
  } catch (e) {
    // Cross-origin error, can't access parent
  }

  const detectedLocale = locale || hashLocale || parentLocale || 'en';
  
  // Debug logging
  console.log('🌍 i18n Debug Info:', {
    url: window.location.href,
    shopifyAppData: shopifyAppData ? {
      currentLocale: shopifyAppData.dataset.currentLocale,
      shopDefaultLocale: shopifyAppData.dataset.shopDefaultLocale
    } : 'Not found',
    searchParams: Object.fromEntries(urlParams),
    hashParams: Object.fromEntries(hashParams),
    detectedLocale,
    availableLocales: ['en', 'es', 'cs', 'da', 'de', 'fi', 'fr', 'it', 'ja', 'ko', 'nb', 'pl', 'pt-BR', 'pt-PT', 'th', 'tr', 'vi', 'zh-CN', 'zh-TW']
  });
  
  return detectedLocale;
};

// Helper function to format numbers according to locale
export const formatNumber = (number, locale = i18n.language, options = {}) => {
  return new Intl.NumberFormat(locale, options).format(number);
};

// Helper function to format currency according to locale
export const formatCurrency = (amount, currency = 'USD', locale = i18n.language) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Helper function to format dates according to locale
export const formatDate = (date, locale = i18n.language, options = {}) => {
  const defaultOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(new Date(date));
};

// Helper function to format datetime according to locale
export const formatDateTime = (datetime, locale = i18n.language, options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(new Date(datetime));
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      cs: { translation: cs },
      da: { translation: da },
      de: { translation: de },
      fi: { translation: fi },
      fr: { translation: fr },
      it: { translation: it },
      ja: { translation: ja },
      ko: { translation: ko },
      nb: { translation: nb },
      pl: { translation: pl },
      'pt-BR': { translation: ptBR },
      'pt-PT': { translation: ptPT },
      th: { translation: th },
      tr: { translation: tr },
      vi: { translation: vi },
      'zh-CN': { translation: zhCN },
      'zh-TW': { translation: zhTW }
    },
    lng: getLocaleFromUrl(),
    fallbackLng: 'en',
    debug: true, // Enable debug mode temporarily
    
    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // Custom formatting functions
    parseMissingKeyHandler: (key) => {
      console.warn(`Missing translation key: ${key}`);
      return key;
    }
  });

// Expose locale change function globally for testing
window.changeLocale = (locale) => {
  console.log(`🌍 Manually changing locale to: ${locale}`);
  i18n.changeLanguage(locale);
};

// Add testing shortcuts
window.testSpanish = () => window.changeLocale('es');
window.testEnglish = () => window.changeLocale('en');

console.log(`🌍 i18n initialized with locale: ${i18n.language}`);
console.log('🧪 Testing commands available:');
console.log('  window.testSpanish() - Switch to Spanish');
console.log('  window.testEnglish() - Switch to English');
console.log('  window.changeLocale("es") - Switch to Spanish');
console.log('  window.changeLocale("en") - Switch to English');

export default i18n; 